'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2CommentResponse, PrV2DiscussionResponse, PrV2ReactionContent } from '@/lib/pr-v2/types';
import type { Reactions } from '@/types/github';
import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { DebouncedReactionEntry, ReactionMutationContext, ReactionMutationVariables } from './reaction-types';
import { createPrV2CommentReaction, deletePrV2CommentReaction } from '../fetch-pr-v2';
import { prV2ReactionMutationKey } from '../mutation-keys';
import { prV2DiscussionQueryKey } from '../query-keys';
import { applyReactionBucketOptimisticState, buildOptimisticReaction, getCommentReactions, getReactionCountFromDiscussion, getReactionRollbackSnapshotFromDiscussion, getReactionUsersFromDiscussion, getUserReactionFromDiscussion, materializeCanonicalReaction, removeReactionById, restoreDeletedReaction, updateOptimisticReactions } from './reaction-cache';
import { createOptimisticReactionId, getDebouncedEntries, getOrCreateCanceledInflightCreateKeys, getOrCreateDebouncedEntries, pruneReactionScope, REACTION_DEBOUNCE_MS, scheduleFallbackInvalidation } from './reaction-state';
import { getCommentNumericId, getCurrentUserGithubId, getReactionKey, normalizeReactionSubjectType } from './reaction-utils';
import { useAuth } from '@/providers/auth-provider';

// Review-body deletes are keyed by review node id + content server-side. The shared client route
// still needs a positive `[reactionId]` segment, so use a benign placeholder for that path piece.
const REVIEW_REACTION_DELETE_PLACEHOLDER_ID = 1;
type ReactionActor = {
  userGithubId: number;
  userLogin: string;
  userAvatarUrl: string;
};
export function usePrV2Reactions(owner, repo, number) {
  const $ = _c(80);
  const queryClient = useQueryClient();
  const {
    user
  } = useAuth();
  let t0;
  if ($[0] !== number || $[1] !== owner || $[2] !== repo) {
    t0 = prV2DiscussionQueryKey(owner, repo, number);
    $[0] = number;
    $[1] = owner;
    $[2] = repo;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const discussionKey = t0;
  let t1;
  if ($[4] !== number || $[5] !== owner || $[6] !== repo) {
    t1 = prV2ReactionMutationKey(owner, repo, number);
    $[4] = number;
    $[5] = owner;
    $[6] = repo;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const reactionMutationKey = t1;
  const prMutationScopeKey = `${owner}/${repo}/${number}`;
  let t2;
  if ($[8] !== discussionKey || $[9] !== queryClient) {
    t2 = () => queryClient.getQueryData(discussionKey);
    $[8] = discussionKey;
    $[9] = queryClient;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const getDiscussion = t2;
  let t3;
  if ($[11] !== user) {
    t3 = comment => {
      const userGithubId = getCurrentUserGithubId(user);
      if (!userGithubId) {
        return null;
      }
      return {
        userGithubId,
        userLogin: user?.user_metadata?.user_name ?? user?.email ?? comment.userLogin ?? "you",
        userAvatarUrl: user?.user_metadata?.avatar_url ?? ""
      };
    };
    $[11] = user;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const getReactionActor = t3;
  let t4;
  if ($[13] !== discussionKey || $[14] !== queryClient) {
    t4 = (commentId, updater) => {
      queryClient.setQueryData(discussionKey, currentDiscussion => {
        if (!currentDiscussion) {
          return currentDiscussion;
        }
        return applyReactionBucketOptimisticState({
          discussion: currentDiscussion,
          reactions: updater(currentDiscussion),
          commentId
        });
      });
    };
    $[13] = discussionKey;
    $[14] = queryClient;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const patchDiscussionReactions = t4;
  let t5;
  if ($[16] !== getDiscussion || $[17] !== user) {
    t5 = (comment_0, emoji) => {
      const commentId_0 = getCommentNumericId(comment_0);
      const userGithubId_0 = getCurrentUserGithubId(user);
      if (!commentId_0 || !userGithubId_0) {
        return null;
      }
      return getUserReactionFromDiscussion({
        discussion: getDiscussion(),
        commentId: commentId_0,
        userGithubId: userGithubId_0,
        emoji
      });
    };
    $[16] = getDiscussion;
    $[17] = user;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const getUserReaction = t5;
  let t6;
  if ($[19] !== getUserReaction) {
    t6 = (comment_1, emoji_0) => Boolean(getUserReaction(comment_1, emoji_0));
    $[19] = getUserReaction;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const isReactionActive = t6;
  let t7;
  if ($[21] !== getDiscussion || $[22] !== user) {
    t7 = (comment_2, emoji_1, reactionId) => {
      const commentId_1 = getCommentNumericId(comment_2);
      const userGithubId_1 = getCurrentUserGithubId(user);
      if (!commentId_1 || !userGithubId_1) {
        return null;
      }
      return getReactionRollbackSnapshotFromDiscussion({
        discussion: getDiscussion(),
        commentId: commentId_1,
        reactionId,
        userGithubId: userGithubId_1,
        emoji: emoji_1
      });
    };
    $[21] = getDiscussion;
    $[22] = user;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const getReactionRollbackSnapshot = t7;
  let t8;
  if ($[24] !== getReactionActor || $[25] !== patchDiscussionReactions) {
    t8 = (comment_3, emoji_2, action, reactionId_0) => {
      const commentId_2 = getCommentNumericId(comment_3);
      const actor = getReactionActor(comment_3);
      if (!commentId_2 || !actor) {
        return;
      }
      const optimisticReactionId = action === "create" ? createOptimisticReactionId() : undefined;
      patchDiscussionReactions(commentId_2, currentDiscussion_0 => updateOptimisticReactions({
        reactions: currentDiscussion_0.reactions,
        commentId: commentId_2,
        emoji: emoji_2,
        action,
        reactionId: reactionId_0,
        optimisticReactionId: action === "create" ? optimisticReactionId ?? createOptimisticReactionId() : undefined,
        userGithubId: actor.userGithubId,
        userLogin: actor.userLogin,
        userAvatarUrl: actor.userAvatarUrl
      }));
      return optimisticReactionId;
    };
    $[24] = getReactionActor;
    $[25] = patchDiscussionReactions;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const applyOptimisticToggle = t8;
  let t9;
  if ($[27] !== number || $[28] !== owner || $[29] !== repo) {
    t9 = async t10 => {
      const {
        comment: comment_4,
        emoji: emoji_3,
        action: action_0,
        reactionId: reactionId_1
      } = t10;
      const commentId_3 = getCommentNumericId(comment_4);
      if (!commentId_3) {
        throw new Error("Invalid comment id for reaction");
      }
      const commentType = normalizeReactionSubjectType(comment_4);
      if (action_0 === "create") {
        const response = await createPrV2CommentReaction(owner, repo, number, commentId_3, {
          commentType,
          content: emoji_3
        });
        return {
          action: action_0,
          commentId: commentId_3,
          reactionId: response.reactionId
        } as const;
      }
      if (!reactionId_1 && commentType !== "pull_request_review") {
        throw new Error("Missing reaction id for delete");
      }
      await deletePrV2CommentReaction(owner, repo, number, commentId_3, reactionId_1 ?? REVIEW_REACTION_DELETE_PLACEHOLDER_ID, commentType, emoji_3);
      return {
        action: action_0,
        commentId: commentId_3
      } as const;
    };
    $[27] = number;
    $[28] = owner;
    $[29] = repo;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== discussionKey || $[32] !== getDiscussion || $[33] !== getReactionActor || $[34] !== patchDiscussionReactions || $[35] !== queryClient || $[36] !== user) {
    t10 = async t11 => {
      const {
        comment: comment_5,
        emoji: emoji_4,
        action: action_1,
        reactionId: reactionId_2,
        _optimisticAlreadyApplied,
        _preAssignedOptimisticReactionId,
        _deletedReaction,
        _deletedReactionIndex
      } = t11;
      const commentId_4 = getCommentNumericId(comment_5);
      if (!commentId_4) {
        return {};
      }
      const userGithubId_2 = getCurrentUserGithubId(user);
      await queryClient.cancelQueries({
        queryKey: discussionKey
      });
      const previousDiscussion = getDiscussion();
      const optimisticReactionId_0 = _preAssignedOptimisticReactionId ?? (action_1 === "create" ? createOptimisticReactionId() : undefined);
      const deletedReaction = _deletedReaction ?? (action_1 === "delete" && userGithubId_2 ? getReactionRollbackSnapshotFromDiscussion({
        discussion: previousDiscussion,
        commentId: commentId_4,
        reactionId: reactionId_2,
        userGithubId: userGithubId_2,
        emoji: emoji_4
      }) : null);
      const deletedReactionEntry = deletedReaction && "reaction" in deletedReaction ? deletedReaction : deletedReaction ? {
        reaction: deletedReaction,
        index: _deletedReactionIndex ?? 0
      } : null;
      if (_optimisticAlreadyApplied) {
        return {
          previousDiscussion,
          commentId: commentId_4,
          optimisticReactionId: optimisticReactionId_0,
          deletedReaction: deletedReactionEntry?.reaction,
          deletedReactionIndex: deletedReactionEntry?.index
        };
      }
      const actor_0 = getReactionActor(comment_5);
      if (!actor_0) {
        return {
          previousDiscussion,
          commentId: commentId_4,
          optimisticReactionId: optimisticReactionId_0,
          deletedReaction: deletedReactionEntry?.reaction,
          deletedReactionIndex: deletedReactionEntry?.index
        };
      }
      patchDiscussionReactions(commentId_4, currentDiscussion_1 => updateOptimisticReactions({
        reactions: currentDiscussion_1.reactions,
        commentId: commentId_4,
        emoji: emoji_4,
        action: action_1,
        reactionId: reactionId_2,
        optimisticReactionId: action_1 === "create" ? optimisticReactionId_0 ?? createOptimisticReactionId() : undefined,
        userGithubId: actor_0.userGithubId,
        userLogin: actor_0.userLogin,
        userAvatarUrl: actor_0.userAvatarUrl
      }));
      return {
        previousDiscussion,
        commentId: commentId_4,
        optimisticReactionId: optimisticReactionId_0,
        deletedReaction: deletedReactionEntry?.reaction,
        deletedReactionIndex: deletedReactionEntry?.index
      };
    };
    $[31] = discussionKey;
    $[32] = getDiscussion;
    $[33] = getReactionActor;
    $[34] = patchDiscussionReactions;
    $[35] = queryClient;
    $[36] = user;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== discussionKey || $[39] !== patchDiscussionReactions || $[40] !== prMutationScopeKey || $[41] !== queryClient) {
    t11 = (error, variables_0, context_0) => {
      const failedCommentId = getCommentNumericId(variables_0.comment);
      if (variables_0.action === "create" && failedCommentId) {
        const reactionKey_0 = getReactionKey(failedCommentId, variables_0.emoji);
        getOrCreateCanceledInflightCreateKeys(prMutationScopeKey).delete(reactionKey_0);
        pruneReactionScope(prMutationScopeKey);
      }
      const commentId_6 = context_0?.commentId;
      if (commentId_6 && context_0?.optimisticReactionId != null) {
        const optimisticReactionId_2 = context_0.optimisticReactionId;
        patchDiscussionReactions(commentId_6, currentDiscussion_2 => removeReactionById({
          reactions: currentDiscussion_2.reactions,
          commentId: commentId_6,
          reactionId: optimisticReactionId_2
        }));
      } else {
        if (commentId_6 && context_0?.deletedReaction && context_0.deletedReactionIndex != null) {
          patchDiscussionReactions(commentId_6, currentDiscussion_3 => restoreDeletedReaction({
            reactions: currentDiscussion_3.reactions,
            commentId: commentId_6,
            reaction: context_0.deletedReaction,
            index: context_0.deletedReactionIndex
          }));
        } else {
          if (context_0?.previousDiscussion) {
            queryClient.setQueryData(discussionKey, context_0.previousDiscussion);
          } else {
            queryClient.removeQueries({
              queryKey: discussionKey,
              exact: true
            });
          }
        }
      }
      toast.error(error instanceof Error ? error.message : "Failed to update reaction");
      scheduleFallbackInvalidation(prMutationScopeKey, queryClient, discussionKey);
    };
    $[38] = discussionKey;
    $[39] = patchDiscussionReactions;
    $[40] = prMutationScopeKey;
    $[41] = queryClient;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  let t12;
  if ($[43] !== discussionKey || $[44] !== prMutationScopeKey || $[45] !== queryClient) {
    t12 = () => {
      scheduleFallbackInvalidation(prMutationScopeKey, queryClient, discussionKey);
    };
    $[43] = discussionKey;
    $[44] = prMutationScopeKey;
    $[45] = queryClient;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  const reactionMutation = useMutation({
    mutationKey: reactionMutationKey,
    mutationFn: t9,
    onMutate: t10,
    onSuccess: (result, variables, context) => {
      if (result.action !== "create") {
        return;
      }
      const commentId_5 = context?.commentId;
      const optimisticReactionId_1 = context?.optimisticReactionId;
      if (!commentId_5 || optimisticReactionId_1 == null) {
        return;
      }
      const canonicalReactionId = result.reactionId;
      if (canonicalReactionId == null) {
        return;
      }
      const reactionKey = getReactionKey(commentId_5, variables.emoji);
      const canceledInflightCreateKeys = getOrCreateCanceledInflightCreateKeys(prMutationScopeKey);
      const canceledInflightCreate = canceledInflightCreateKeys.delete(reactionKey);
      pruneReactionScope(prMutationScopeKey);
      if (canceledInflightCreate) {
        const actor_1 = getReactionActor(variables.comment);
        if (!actor_1) {
          return;
        }
        const deletedReactionIndex = getCommentReactions(getDiscussion(), commentId_5).length;
        reactionMutation.mutateAsync({
          comment: variables.comment,
          emoji: variables.emoji,
          action: "delete",
          reactionId: canonicalReactionId,
          _optimisticAlreadyApplied: true,
          _deletedReaction: buildOptimisticReaction(canonicalReactionId, variables.emoji, actor_1.userGithubId, actor_1.userLogin, actor_1.userAvatarUrl),
          _deletedReactionIndex: deletedReactionIndex
        });
        return;
      }
      const actor_2 = getReactionActor(variables.comment);
      if (!actor_2) {
        return;
      }
      patchDiscussionReactions(commentId_5, discussion => materializeCanonicalReaction({
        reactions: discussion.reactions,
        commentId: commentId_5,
        canonicalReactionId,
        emoji: variables.emoji,
        userGithubId: actor_2.userGithubId,
        userLogin: actor_2.userLogin,
        userAvatarUrl: actor_2.userAvatarUrl
      }));
    },
    onError: t11,
    onSettled: t12
  });
  let t13;
  if ($[47] !== reactionMutationKey) {
    t13 = {
      filters: {
        mutationKey: reactionMutationKey,
        status: "pending"
      },
      select: _temp
    };
    $[47] = reactionMutationKey;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  const pendingReactionVariables = useMutationState(t13);
  let t14;
  if ($[49] !== pendingReactionVariables) {
    t14 = pendingReactionVariables.reduce(_temp2, {});
    $[49] = pendingReactionVariables;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  const pendingReactionCountByComment = t14;
  let t15;
  if ($[51] !== pendingReactionVariables) {
    t15 = pendingReactionVariables.reduce(_temp3, {});
    $[51] = pendingReactionVariables;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  const pendingReactionCountByCommentAndEmoji = t15;
  let t16;
  if ($[53] !== prMutationScopeKey || $[54] !== reactionMutation) {
    t16 = entry => {
      const commentId_9 = getCommentNumericId(entry.comment);
      if (!commentId_9) {
        return;
      }
      const reactionKey_2 = getReactionKey(commentId_9, entry.emoji);
      getDebouncedEntries(prMutationScopeKey)?.delete(reactionKey_2);
      pruneReactionScope(prMutationScopeKey);
      if (entry.netAction === null) {
        return;
      }
      if (entry.netAction === "create" && entry.serverHadReaction) {
        return;
      }
      if (entry.netAction === "delete" && !entry.serverHadReaction) {
        return;
      }
      reactionMutation.mutateAsync({
        comment: entry.comment,
        emoji: entry.emoji,
        action: entry.netAction,
        reactionId: entry.netAction === "delete" ? entry.serverReactionId : undefined,
        _optimisticAlreadyApplied: true,
        _preAssignedOptimisticReactionId: entry.netAction === "create" ? entry.optimisticReactionId : undefined,
        _deletedReaction: entry.deletedReaction,
        _deletedReactionIndex: entry.deletedReactionIndex
      });
    };
    $[53] = prMutationScopeKey;
    $[54] = reactionMutation;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  const flushDebouncedReaction = t16;
  let t17;
  if ($[56] !== applyOptimisticToggle || $[57] !== flushDebouncedReaction || $[58] !== getReactionRollbackSnapshot || $[59] !== getUserReaction || $[60] !== patchDiscussionReactions || $[61] !== pendingReactionCountByCommentAndEmoji || $[62] !== prMutationScopeKey) {
    t17 = (comment_8, emoji_6) => {
      const commentId_10 = getCommentNumericId(comment_8);
      if (!commentId_10) {
        return;
      }
      const reactionKey_3 = getReactionKey(commentId_10, emoji_6);
      const pendingReactionCount = pendingReactionCountByCommentAndEmoji[reactionKey_3] ?? 0;
      const canceledInflightCreateKeys_0 = getOrCreateCanceledInflightCreateKeys(prMutationScopeKey);
      const prEntries = getOrCreateDebouncedEntries(prMutationScopeKey);
      const existingEntry = prEntries.get(reactionKey_3);
      if (existingEntry) {
        clearTimeout(existingEntry.timer);
        const currentReaction = getUserReaction(comment_8, emoji_6);
        const currentlyActive = Boolean(currentReaction);
        const nextAction = currentlyActive ? "delete" : "create";
        if (nextAction === "create" && existingEntry.serverHadReaction && existingEntry.deletedReaction) {
          patchDiscussionReactions(commentId_10, currentDiscussion_4 => restoreDeletedReaction({
            reactions: currentDiscussion_4.reactions,
            commentId: commentId_10,
            reaction: existingEntry.deletedReaction,
            index: existingEntry.deletedReactionIndex ?? 0
          }));
          existingEntry.netAction = null;
          existingEntry.optimisticReactionId = undefined;
        } else {
          const rollbackSnapshot = nextAction === "delete" ? getReactionRollbackSnapshot(comment_8, emoji_6, currentReaction?.id) : null;
          const optimisticId = applyOptimisticToggle(comment_8, emoji_6, nextAction, rollbackSnapshot?.reaction.id);
          existingEntry.deletedReaction = rollbackSnapshot?.reaction;
          existingEntry.deletedReactionIndex = rollbackSnapshot?.index;
          existingEntry.netAction = nextAction === "create" ? existingEntry.serverHadReaction ? null : "create" : existingEntry.serverHadReaction ? "delete" : null;
          if (optimisticId !== undefined) {
            existingEntry.optimisticReactionId = optimisticId;
          }
        }
        existingEntry.timer = setTimeout(() => flushDebouncedReaction(existingEntry), REACTION_DEBOUNCE_MS);
        return;
      }
      const existingReaction = getUserReaction(comment_8, emoji_6);
      const currentlyActive_0 = Boolean(existingReaction);
      if (existingReaction && existingReaction.id <= 0) {
        if (pendingReactionCount > 0) {
          applyOptimisticToggle(comment_8, emoji_6, "delete", existingReaction.id);
          canceledInflightCreateKeys_0.add(reactionKey_3);
        }
        return;
      }
      if (!currentlyActive_0 && pendingReactionCount > 0 && canceledInflightCreateKeys_0.has(reactionKey_3)) {
        canceledInflightCreateKeys_0.delete(reactionKey_3);
        pruneReactionScope(prMutationScopeKey);
        applyOptimisticToggle(comment_8, emoji_6, "create");
        return;
      }
      const action_2 = currentlyActive_0 ? "delete" : "create";
      const rollbackSnapshot_0 = action_2 === "delete" ? getReactionRollbackSnapshot(comment_8, emoji_6, existingReaction?.id) : null;
      const optimisticId_0 = applyOptimisticToggle(comment_8, emoji_6, action_2, rollbackSnapshot_0?.reaction.id);
      const entry_0 = {
        netAction: action_2,
        serverHadReaction: currentlyActive_0,
        serverReactionId: existingReaction?.id,
        optimisticReactionId: optimisticId_0,
        comment: comment_8,
        emoji: emoji_6,
        deletedReaction: rollbackSnapshot_0?.reaction,
        deletedReactionIndex: rollbackSnapshot_0?.index,
        timer: setTimeout(() => flushDebouncedReaction(entry_0), REACTION_DEBOUNCE_MS)
      };
      prEntries.set(reactionKey_3, entry_0);
    };
    $[56] = applyOptimisticToggle;
    $[57] = flushDebouncedReaction;
    $[58] = getReactionRollbackSnapshot;
    $[59] = getUserReaction;
    $[60] = patchDiscussionReactions;
    $[61] = pendingReactionCountByCommentAndEmoji;
    $[62] = prMutationScopeKey;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  const toggleReaction = t17;
  let t18;
  if ($[64] !== getDiscussion) {
    t18 = (comment_9, emoji_7) => {
      const commentId_11 = getCommentNumericId(comment_9);
      if (!commentId_11) {
        return 0;
      }
      return getReactionCountFromDiscussion({
        discussion: getDiscussion(),
        commentId: commentId_11,
        emoji: emoji_7
      });
    };
    $[64] = getDiscussion;
    $[65] = t18;
  } else {
    t18 = $[65];
  }
  const getReactionCount = t18;
  let t19;
  if ($[66] !== getDiscussion) {
    t19 = (comment_10, emoji_8) => {
      const commentId_12 = getCommentNumericId(comment_10);
      if (!commentId_12) {
        return [];
      }
      return getReactionUsersFromDiscussion({
        discussion: getDiscussion(),
        commentId: commentId_12,
        emoji: emoji_8
      });
    };
    $[66] = getDiscussion;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  const getReactionUsers = t19;
  let t20;
  if ($[68] !== pendingReactionCountByComment) {
    t20 = comment_11 => {
      const commentId_13 = getCommentNumericId(comment_11);
      if (!commentId_13) {
        return false;
      }
      return (pendingReactionCountByComment[commentId_13] ?? 0) > 0;
    };
    $[68] = pendingReactionCountByComment;
    $[69] = t20;
  } else {
    t20 = $[69];
  }
  const isPendingForComment = t20;
  let t21;
  if ($[70] !== pendingReactionCountByCommentAndEmoji) {
    t21 = (comment_12, emoji_9) => {
      const commentId_14 = getCommentNumericId(comment_12);
      if (!commentId_14) {
        return false;
      }
      return (pendingReactionCountByCommentAndEmoji[getReactionKey(commentId_14, emoji_9)] ?? 0) > 0;
    };
    $[70] = pendingReactionCountByCommentAndEmoji;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  const isPendingForReaction = t21;
  let t22;
  if ($[72] !== getReactionCount || $[73] !== getReactionUsers || $[74] !== isPendingForComment || $[75] !== isPendingForReaction || $[76] !== isReactionActive || $[77] !== reactionMutation.isPending || $[78] !== toggleReaction) {
    t22 = {
      toggleReaction,
      getReactionCount,
      getReactionUsers,
      isReactionActive,
      isPendingForComment,
      isPendingForReaction,
      isPending: reactionMutation.isPending
    };
    $[72] = getReactionCount;
    $[73] = getReactionUsers;
    $[74] = isPendingForComment;
    $[75] = isPendingForReaction;
    $[76] = isReactionActive;
    $[77] = reactionMutation.isPending;
    $[78] = toggleReaction;
    $[79] = t22;
  } else {
    t22 = $[79];
  }
  return t22;
}
function _temp3(countsByReaction, variables_2) {
  const comment_7 = variables_2?.comment;
  const commentId_8 = comment_7 ? getCommentNumericId(comment_7) : null;
  const emoji_5 = variables_2?.emoji;
  if (!commentId_8 || !emoji_5) {
    return countsByReaction;
  }
  const reactionKey_1 = getReactionKey(commentId_8, emoji_5);
  countsByReaction[reactionKey_1] = (countsByReaction[reactionKey_1] ?? 0) + 1;
  return countsByReaction;
}
function _temp2(countsByCommentId, variables_1) {
  const comment_6 = variables_1?.comment;
  const commentId_7 = comment_6 ? getCommentNumericId(comment_6) : null;
  if (!commentId_7) {
    return countsByCommentId;
  }
  countsByCommentId[commentId_7] = (countsByCommentId[commentId_7] ?? 0) + 1;
  return countsByCommentId;
}
function _temp(mutation) {
  return mutation.state.variables as ReactionMutationVariables | undefined;
}